import React from "react";
import { Link as RouterLink, NavLink } from "react-router-dom";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardHeader,
  Divider,
  Hidden,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatDistance } from "date-fns";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import reportWebVitals from "../../reportWebVitals";
import { Application } from "../apps-components/apps-types";
import NoDataToBeShown from "../reusable/NoDataToBeShown";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
/* import convertUTCDateToLocalDate from "../../utils/fromUTCtoLocalTime"; */
import { Skeleton4LastActivities } from "../dashboard-components/LastActivities";
import { enUS, it } from "date-fns/locale";
import checkPlatform from "../../utils/checkPlatform";

export function AppItem(props: { app: Application }) {
  const { t } = useTranslation();

  return props.app ? (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <NavLink
        to={"/applications/" + props.app.name}
        style={{ textDecoration: "none" }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Badge
            badgeContent={
              <PhoneIphoneIcon sx={{ color: "white", width: 18, height: 18 }} />
            }
            color="info"
            overlap="circular"
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            sx={{ backgroundColor: "white" }}
          >
            <Avatar
              alt={props.app.name}
              src={`${process.env.REACT_APP_BACKEND_BASE_URL}apps/${props.app.id}/icon`}
              sx={{ width: 40, height: 40, borderRadius: 1.5 }}
            />
          </Badge>
          <Box sx={{ width: 280 }}>
            <Stack direction="row" spacing={1}>
              <Typography
                variant="subtitle2"
                sx={{ color: "text.primary", textDecoration: "none" }}
                noWrap
              >
                {props.app.name}
              </Typography>
              <Stack direction="row">
                {(checkPlatform(props.app.versions) === "Android; iOS;" ||
                  checkPlatform(props.app.versions) === "iOS") && (
                  <AppleIcon color="action" sx={{ width: 18, height: 18 }} />
                )}
                {(checkPlatform(props.app.versions) === "Android; iOS;" ||
                  checkPlatform(props.app.versions) === "Android") && (
                  <AndroidIcon color="action" sx={{ width: 18, height: 18 }} />
                )}
              </Stack>
            </Stack>
            <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
              {props.app.description.length > 35
                ? props.app.description.slice(0, 35) + "..."
                : props.app.description}
            </Typography>
          </Box>
          <Hidden smDown>
            <Box sx={{ alignItems: "center", width: 280 }}>
              <Typography
                variant="caption"
                sx={{
                  pr: 3,
                  flexShrink: 0,
                  color: "text.secondary",
                  textAlign: "center",
                }}
              >
                {props.app.versions.length === 1
                  ? t("New application")
                  : t("New version")}
              </Typography>
            </Box>
          </Hidden>
          <Hidden smDown>
            <Box sx={{ alignItems: "center", width: 280 }}>
              <Typography
                variant="caption"
                sx={{
                  pr: 3,
                  flexShrink: 0,
                  color: "text.secondary",
                  textAlign: "center",
                }}
              >
                {formatDistance(new Date(props.app.updated_at), new Date(), {
                  locale: localStorage.getItem("lng") === "it" ? it : enUS,
                })}
              </Typography>
            </Box>
            <Box sx={{ alignItems: "center", width: 280 }}>
              <Typography
                variant="caption"
                sx={{
                  pr: 3,
                  flexShrink: 0,
                  color: "text.secondary",
                  textAlign: "center",
                }}
              >
                {props.app.versions.sort((a, b) =>
                  new Date(a.updated_at).getTime() <
                  new Date(b.updated_at).getTime()
                    ? 1
                    : -1
                )[props.app.versions.length - 1].changelog.length > 35
                  ? props.app.versions[
                      props.app.versions.length - 1
                    ].changelog.slice(0, 35) + "..."
                  : props.app.versions[props.app.versions.length - 1].changelog}
              </Typography>
            </Box>
          </Hidden>
        </Stack>
      </NavLink>
    </Stack>
  ) : (
    <Skeleton4LastActivities />
  );
}

function LastApps(props: { reverseData: Application[]; isLoading: boolean }) {
  const { t } = useTranslation();
  const skeletonArray = Array(3).fill("");

  return (
    <Card sx={{ boxShadow: 5, borderRadius: 1.5 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        mr={1}
        alignItems="center"
      >
        <CardHeader title={t("Last Applications")} />
      </Stack>

      {props.isLoading ? (
        <Stack spacing={3} sx={{ p: 3, pr: 0 }}>
          {skeletonArray.map((skeleton, index) => (
            <Skeleton4LastActivities key={index} />
          ))}
        </Stack>
      ) : props.reverseData.length > 0 ? (
        <Stack spacing={3} sx={{ p: 3, pr: 0 }}>
          {props.reverseData.map((item, index) => (
            <AppItem key={index} app={item} />
          ))}
        </Stack>
      ) : (
        <NoDataToBeShown />
      )}
      <Divider />

      <Box sx={{ p: 2, textAlign: "right" }}>
        <Button
          to="/applications"
          size="small"
          color="inherit"
          component={RouterLink}
          endIcon={<ArrowForwardIosIcon />}
        >
          {t("view_all")}
        </Button>
      </Box>
    </Card>
  );
}

export default LastApps;

reportWebVitals();
